// ConnectionManager.ts
import { GanttLinks } from '@/ship/Enums/GanttEnum';
import { PMStructureLinkLocationEnum } from '@/ship/Enums/ProjectManagementEnums';
import { Link } from '@/ship/Models/ProjectManagement';
import { createLinkStructure, getTaskByIdStructure, updateLinkStructure } from '@/utils/api/ProjectManagement/PMChart';
import ErrorService from '@/utils/ErrorService';
import { compareVersionStrings } from '@/utils/ProjectManagement/compareVersionStrings';
import { LinkManager } from './LinkManager';

export class ConnectionManager {
    private links: LinkManager[] = [];
    private deletedLinks: LinkManager[] = [];

    constructor(private activeTaskId: number, private currentChartId: number) {
        this.initLinks();
    }

    get createdLinks(): LinkManager[] {
        return this.links.filter((link) => link.isNewLink && !link.hasBeenDeleted).reverse();
    }

    get innerLinks(): LinkManager[] {
        const links = this.links.filter(
            (link) =>
                link.location === PMStructureLinkLocationEnum.ThisChart && !link.isNewLink && !link.hasBeenDeleted,
        );

        const hasTarget = this.links.every((link: LinkManager) => link.targetTask);

        return hasTarget
            ? links.sort((first, second) => compareVersionStrings(first.targetTask!.code, second.targetTask!.code))
            : links;
    }

    get outerLinks(): LinkManager[] {
        const links = this.links.filter(
            (link) =>
                link.location === PMStructureLinkLocationEnum.OtherChart && !link.isNewLink && !link.hasBeenDeleted,
        );

        const hasTarget = this.links.every((link: LinkManager) => link.targetTask);

        return hasTarget
            ? links.sort((first, second) => compareVersionStrings(first.targetTask!.code, second.targetTask!.code))
            : links;
    }

    get hasUpdatedLinks() {
        const deleted = this.deletedLinks.filter((link) => link.id);

        return [...this.links, ...deleted].some((link) => link.hasUpdated);
    }

    private async initLinks(): Promise<void> {
        try {
            const response = await getTaskByIdStructure(this.activeTaskId, {
                include: 'links.targetTask,links.targetChart',
            });

            this.setupLinks(response.data.data.links.data);
        } catch (err) {
            ErrorService.handleApiError(err);
        }
    }

    private setupLinks(links: Link[]): void {
        this.links = links.map(
            (link) =>
                new LinkManager(
                    {
                        id: link.id,
                        connectionType: link.type,
                        delay: link.delay,
                        selectedTaskId: link.targetTaskId,
                        chartId: link.targetChartId,
                        location:
                            this.currentChartId === link.targetChartId
                                ? PMStructureLinkLocationEnum.ThisChart
                                : PMStructureLinkLocationEnum.OtherChart,
                        isLoading: false,
                        taskError: '',
                        connectionTypeError: '',
                    },
                    this.currentChartId,
                    (data: any) => updateLinkStructure(link.id!, data),
                    link.targetTask!.data,
                    link.targetChart?.data,
                ),
        );
    }

    createLink(): LinkManager {
        const link = new LinkManager(
            {
                id: null,
                location: PMStructureLinkLocationEnum.ThisChart,
                connectionType: GanttLinks.EndStart,
                delay: null,
                selectedTaskId: null,
                taskError: '',
                connectionTypeError: '',
                chartId: this.currentChartId,
                isNewLink: true,
                isLoading: false,
            },
            this.currentChartId,
            createLinkStructure,
        );

        this.links.push(link);
        return link;
    }

    deleteLink(link: LinkManager): void {
        link.hasBeenDeleted = true;
        if (link.id) link.setUpdatedState();

        const index = this.links.findIndex((_link) => _link.id === link.id);
        this.links.splice(index, 1);

        this.deletedLinks.push(link);
    }

    async handleSaveAction(): Promise<void> {
        const requests = this.links
            .filter((link) => !link.hasBeenDeleted)
            .map((link) => link.handleRequest(this.activeTaskId));

        await Promise.all(requests);
    }

    async handleDeleteAction(): Promise<void> {
        const requests = this.deletedLinks.map((link) => link.handleDeletingLink());

        await Promise.all(requests);
    }

    async handleAllActions() {
        await this.handleDeleteAction();
        await this.handleSaveAction();
    }

    findLinkById(id: number): LinkManager | undefined {
        return this.links.find((link) => link.id === id);
    }

    restoreLink(link: LinkManager): void {
        link.hasBeenDeleted = false;
        const index = this.deletedLinks.indexOf(link);
        if (index > -1) {
            this.deletedLinks.splice(index, 1);
        }
    }
}
